import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/hooks/use-role";

/**
 * Aviso para superadmin sem verificação em duas etapas.
 *
 * POR QUE UM AVISO E NÃO UM BLOQUEIO
 * ----------------------------------
 *
 * A exigência de segundo fator para superadmin está construída no banco
 * (`superadmin_com_mfa()`), mas NÃO ligada — nenhum dos superadmins tem fator
 * cadastrado, e ligar agora os trancaria para fora da administração.
 *
 * A ordem certa é capacidade → aviso → exigência. Este componente é o passo do
 * meio, e ele não é opcional: sem ele, a capacidade fica sendo código que
 * ninguém usa, e a exigência nunca chega.
 *
 * O aviso não é dispensável de propósito. É a conta com mais poder do sistema —
 * comprometê-la dá acesso a banco, chaves e funções. Um "lembrar depois" aqui
 * significa nunca.
 */
export function AvisoMfaSuperadmin() {
  const { isSuperAdmin } = useRole();

  const { data: temMfa, isLoading } = useQuery({
    queryKey: ["mfa-ativo"],
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return (data?.totp ?? []).some((f) => f.status === "verified");
    },
    enabled: isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });

  if (!isSuperAdmin || isLoading || temMfa !== false) return null;

  return (
    <div
      role="alert"
      className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 p-4"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 shrink-0 text-destructive mt-0.5" aria-hidden />
        <div className="flex-1 text-sm">
          <p className="font-semibold text-foreground">
            Sua conta de superadmin não tem verificação em duas etapas
          </p>
          <p className="mt-1 text-muted-foreground">
            É a conta com mais poder do sistema: quem a compromete alcança o banco
            de dados, as chaves e as funções. Só a senha protege isso hoje.
          </p>
          <Link
            to="/configuracoes"
            className="mt-2 inline-block font-medium text-destructive underline hover:no-underline"
          >
            Ativar agora, em Configurações
          </Link>
        </div>
      </div>
    </div>
  );
}
